import { Map as MapIcon } from '@mui/icons-material';
import { Box, Fab, Grid, Link as MuiLink, Stack, Typography } from '@mui/material';
import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import InfoBanner from '/@/components/bayer/info-banner';
import Icon from '/@/components/icon';
import IncidenceMapsOverlay from '/@/components/incidence-maps-overlay';
import IncidenceMapsOverlayReferences from '/@/components/incidence-maps-overlay-references';
import MapComponent from '/@/components/map';
import settings, { isExpertoBayer } from '/@/core/settings';
import { _ } from '/@/locale';
import { checkInternal } from '/@/settings/menu.js';
import { getAppInstance, getUser } from '/@/store/session/selectors';
import type { Field } from '/@/types/field';
import useIsMobile from '/@/utils/hooks/is-mobile';
import { reverse } from '/@/utils/router';
import { ONBOARING_IDS } from '/@/views/dashboard/rv/home';
import { FrostRiskView } from '/@/views/frost-risk';
import FRReferences from '/@/views/frost-risk/frost-risk-map/components/references';

const FieldMap = ({
    showFrostRiskMap,
    handleMarkerClick,
    fields,
    selectedField,
    selectedOverlay,
    handleOverlayChange,
    showMaps,
    parseOverlay,
    overlayData,
    setMapRef,
    maps,
    offline,
    humidityMaps,
    handleHumidityClick,
    handleShowFrostRisk,
    handleShowMaps,
    showTrialNetwork,
    mapsEnabled,
}: {
    showFrostRiskMap: boolean;
    handleMarkerClick: (field: Field) => void;
    fields: Field[];
    selectedField: Field;
    selectedOverlay: any;
    setMapRef: (ref: any) => void;
    offline: boolean;
    handleOverlayChange: (overlay: any) => void;
    showMaps: boolean;
    parseOverlay: (overlay: any) => any;
    overlayData: any;
    maps: any;
    humidityMaps: any;
    handleHumidityClick: () => void;
    handleShowFrostRisk: () => void;
    handleShowMaps: () => void;
    showTrialNetwork: boolean;
    mapsEnabled: boolean;
}) => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const parseHumidityReferences = (references: { value: string; color: string }[] = []) => {
        return references.map((reference) => {
            return {
                label: reference.value,
                color: `#${reference.color}`,
            };
        });
    };

    const appInstance = useSelector(getAppInstance);
    const user = useSelector(getUser);
    return (
        <>
            {showFrostRiskMap && (
                <FrostRiskView
                    id={0}
                    isHome
                    fields={fields}
                    selectedField={selectedField}
                    handleSelectField={(f: Field) => {
                        handleMarkerClick({ id: f.id });
                    }}
                />
            )}

            <Box
                sx={{
                    visibility: !showFrostRiskMap ? 'visible' : 'hidden',
                    height: showFrostRiskMap && !isMobile ? '100%' : undefined,
                    width: '100%',
                }}
            >
                <MapComponent
                    ref={setMapRef}
                    fields={fields}
                    onFieldClick={handleMarkerClick}
                    offline={offline}
                    overlay={parseOverlay(selectedOverlay)}
                    onOverlayChange={handleOverlayChange}
                    noGeocoder
                    simplifyPolygons
                    selectedField={selectedField}
                    zoomControls={!isExpertoBayer()}
                    onOverlayClose={() => {
                        handleOverlayChange(undefined);
                    }}
                />
                {showMaps && (
                    <IncidenceMapsOverlay
                        field={selectedField}
                        overlayConfig={overlayData.children?.[0]?.children || []}
                        maps={maps}
                        setSelectedOverlay={(overlay) => handleOverlayChange(overlay)}
                    />
                )}
            </Box>

            {selectedOverlay && !showFrostRiskMap && !showMaps && isMobile && (
                <FRReferences
                    references={parseHumidityReferences(selectedOverlay.version.referenceItems)}
                    title={_('Humedad en el suelo')}
                    source={_(selectedOverlay.version.source)}
                    showInfoIcon={false}
                    sx={{
                        flexDirection: 'column',
                        width: { xs: 200, md: 350 },
                        alignItems: 'flex-start',
                        pointerEvents: 'auto',
                        zIndex: 1,
                        position: 'absolute',
                        bottom: '22px',
                    }}
                />
            )}

            {selectedOverlay && showMaps && isMobile && (
                <IncidenceMapsOverlayReferences
                    {...selectedOverlay}
                    onOverlayChange={handleOverlayChange}
                    sx={{
                        flexDirection: 'column',
                        width: '100%',
                        alignItems: 'flex-start',
                        pointerEvents: 'auto',
                        zIndex: 1051,
                        position: 'absolute',
                        bottom: '76px',
                    }}
                />
            )}

            {isExpertoBayer() && !!humidityMaps?.length && (
                <Fragment>
                    <Stack
                        id={ONBOARING_IDS.FIFTH}
                        direction='column'
                        spacing={2}
                        sx={{ position: 'absolute', right: '10px', top: { xs: 10, md: 110 } }}
                    >
                        <Fab
                            variant='extended'
                            sx={{
                                borderRadius: '10px',
                                height: 90,
                                width: 90,
                                textTransform: 'none',
                                background: (t) => (selectedOverlay && !showMaps ? t.palette.secondary.light : 'white'),
                                ...(selectedOverlay &&
                                    !showMaps && {
                                        border: (t) => `1px solid ${t.palette.secondary.main}`,
                                    }),
                                '&:hover': {
                                    background: (t) => (selectedOverlay && !showMaps ? '' : t.palette.primary.light),
                                },
                            }}
                            onClick={handleHumidityClick}
                        >
                            <Grid container direction='column'>
                                <Grid item sx={{ width: 20, m: 'auto' }}>
                                    <Icon
                                        type='humidity'
                                        theme={settings.app.theme}
                                        variant={selectedOverlay && !showMaps ? 'secondary' : 'primary'}
                                    />
                                </Grid>
                                <Grid item>
                                    <Typography variant='caption' fontWeight={500}>
                                        {_('Humedad de Suelo')}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Fab>
                        <Fab
                            variant='extended'
                            onClick={handleShowFrostRisk}
                            sx={{
                                borderRadius: '10px',
                                height: 90,
                                width: 90,
                                textTransform: 'none',
                                '&:hover': {
                                    background: (t) => (showFrostRiskMap ? '' : t.palette.primary.light),
                                },
                                background: (t) => (showFrostRiskMap ? t.palette.secondary.light : 'white'),
                                ...(showFrostRiskMap && {
                                    border: (t) => `1px solid ${t.palette.secondary.main}`,
                                }),
                            }}
                        >
                            <Grid container direction='column'>
                                <Grid item sx={{ width: 30, m: 'auto' }}>
                                    <Icon
                                        type='frost'
                                        theme={settings.app.theme}
                                        variant={showFrostRiskMap ? 'secondary' : 'primary'}
                                    />
                                </Grid>
                                <Grid item>
                                    <Typography variant='caption' fontWeight={500}>
                                        {_('Riesgo de Heladas')}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Fab>
                        {mapsEnabled && (
                            <Fab
                                variant='extended'
                                onClick={handleShowMaps}
                                sx={{
                                    borderRadius: '10px',
                                    height: 90,
                                    width: 90,
                                    textTransform: 'none',
                                    '&:hover': {
                                        background: (t) => (showMaps ? '' : t.palette.primary.light),
                                    },
                                    background: (t) => (showMaps ? t.palette.secondary.light : 'white'),
                                    ...(showMaps && {
                                        outline: (t) => `1px solid ${t.palette.secondary.main}`,
                                    }),
                                }}
                            >
                                <Grid container direction='column' alignContent='center'>
                                    <Grid item sx={{ width: 30, m: 'auto' }}>
                                        <Icon
                                            size='small'
                                            type='diseases'
                                            theme={settings.app.theme}
                                            variant={showMaps ? 'secondary' : 'primary'}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <Typography variant='caption' fontWeight={500}>
                                            {_('Problemas')}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Fab>
                        )}
                    </Stack>

                    {showTrialNetwork && checkInternal(user, appInstance) && (
                        <InfoBanner
                            sx={{
                                bottom: 24,
                                left: 24,
                                position: 'absolute',
                            }}
                            icon={
                                <MapIcon
                                    sx={{
                                        width: '24px',
                                        height: '24px',
                                        fill: (t) => t.palette.primary.mui.inactive,
                                    }}
                                />
                            }
                            elevation={1}
                            color='inactive'
                        >
                            <MuiLink sx={{ cursor: 'pointer' }} onClick={() => navigate(reverse('FieldTrialNetwork'))}>
                                {_('Ver Red de ensayos')}
                            </MuiLink>
                        </InfoBanner>
                    )}
                </Fragment>
            )}
        </>
    );
};

export default FieldMap;
